import { toIsoFromLocalInput } from '../../pages/incidents/useIncidentsTimeRange';
import { formatFollowUpStatusDisplayLabel } from './followUpStatusSemantics';
import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';

export type CorrectiveActionFollowThroughGuide = {
  planSummary: string;
  steps: string[];
  channelNote?: string;
};

/** Buyer-facing follow-through for each corrective action type (warehouse demo; plan metadata only). */
export const CORRECTIVE_ACTION_FOLLOW_THROUGH: Record<string, CorrectiveActionFollowThroughGuide> = {
  signage: {
    planSummary: 'Add or update signage so pedestrians and forklift traffic are clearly separated.',
    steps: [
      'Assign action owner accountable for the change.',
      'Create action / work order if your site tracks tasks in Odoo.',
      'Set due date and time for installation or verification walk.',
      'Set next review time to confirm signage is in place.',
      'Record impact measurement in the review window after the change.',
    ],
  },
  pathing: {
    planSummary: 'Adjust pedestrian pathing or barriers so pedestrians do not cut through the forklift zone.',
    steps: [
      'Assign action owner.',
      'Optional work order for layout / operations change.',
      'Due date and time for the pathing change.',
      'Next review time to verify foot traffic patterns.',
      'Impact measurement compares observation volume before vs after.',
    ],
  },
  floor_markings: {
    planSummary: 'Refresh floor markings or lane boundaries in the affected area.',
    steps: [
      'Assign action owner.',
      'Work order for facilities if needed.',
      'Due date and time for marking work.',
      'Next review to confirm markings are visible on camera.',
      'Impact measurement after the review window.',
    ],
  },
  training: {
    planSummary: 'Brief the shift team on pedestrian–forklift separation for this zone.',
    steps: [
      'Assign action owner (often supervisor or trainer).',
      'Due date and time for the briefing.',
      'Next review to confirm training occurred.',
      'Impact measurement to see if observations drop.',
    ],
  },
  supervisor_notify: {
    planSummary: 'Escalate this recurring risk to the site supervisor for operational follow-up.',
    steps: [
      'Notify the supervisor through the site’s normal process.',
      'Ask the supervisor to acknowledge the risk.',
      'Set a next review date.',
      'Track whether the recurring risk pattern decreases after action.',
    ],
    channelNote:
      'This page records the corrective plan on this review item. It does not send email or SMS automatically. Use your site notification channel to reach the supervisor.',
  },
  zone_config: {
    planSummary: 'Tune zone geometry, policy thresholds, or camera alignment for this detection.',
    steps: [
      'Assign action owner (often safety + engineering).',
      'Run guided triage or policy editor if the detection may be misconfigured.',
      'Due date and time for config change / redeploy.',
      'Next review after policies are live.',
      'Impact measurement to validate false positives vs real risk.',
    ],
  },
};

export function correctiveActionFollowThroughForType(
  value: string | null | undefined,
): CorrectiveActionFollowThroughGuide | null {
  const raw = String(value || '').trim().toLowerCase();
  return CORRECTIVE_ACTION_FOLLOW_THROUGH[raw] || null;
}

export function formatGroupedCorrectiveDateTimeDisplay(
  localInput: string | null | undefined,
  persistedIso?: string | null,
): string | null {
  const iso = toIsoFromLocalInput(String(localInput || '')) || String(persistedIso || '').trim();
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const GROUPED_CORRECTIVE_NEXT_REVIEW_DATE_HELPER =
  'Schedule when to check whether follow-up is working and recurring pattern observations are decreasing.';

export const GROUPED_SUPERVISOR_RECOMMENDED_ACTION_HEADLINE =
  'Recommended action: Escalate this recurring risk to the site supervisor for operational follow-up.';

export const CORRECTIVE_ACTION_TYPE_OPTIONS = [
  { value: 'signage', label: 'Signage' },
  { value: 'pathing', label: 'Pathing / routing' },
  { value: 'floor_markings', label: 'Floor markings' },
  { value: 'training', label: 'Training' },
  { value: 'supervisor_notify', label: 'Supervisor notification' },
  { value: 'zone_config', label: 'Zone configuration' },
] as const;

export const CORRECTIVE_ACTION_PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;

export function correctiveActionTypeLabel(value: string | null | undefined): string {
  const raw = String(value || '').trim().toLowerCase();
  const match = CORRECTIVE_ACTION_TYPE_OPTIONS.find((option) => option.value === raw);
  if (match) return match.label;
  return raw ? raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'Not set';
}

export function correctiveActionPriorityLabel(value: string | null | undefined): string {
  const raw = String(value || '').trim().toLowerCase();
  const match = CORRECTIVE_ACTION_PRIORITY_OPTIONS.find((option) => option.value === raw);
  if (match) return match.label;
  return raw ? raw.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : 'Not set';
}

export function parseLocalDateTimeInput(value: string | null | undefined): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/** @deprecated Grouped review uses deriveGroupedCorrectivePlanStatusLabel instead. */
export function deriveCorrectiveActionStatus(args: {
  correctiveActionLocked?: boolean;
  followUpOwnerLabel?: string | null;
  opsState?: string | null;
  hasWorkOrder?: boolean;
  dueDateInput?: string | null;
  nowMs?: number;
}): string {
  if (args.correctiveActionLocked) return 'Locked';
  if (ownerRoleIsUnassigned(args.followUpOwnerLabel)) return 'Pending assignment';

  const opsState = String(args.opsState || '').trim().toLowerCase();
  if (opsState === 'closed') return 'Completed';
  if (opsState === 'in_progress' || opsState === 'acknowledged' || opsState === 'resolved') {
    return 'In progress';
  }

  const dueAt = parseLocalDateTimeInput(args.dueDateInput);
  const nowMs = Number.isFinite(Number(args.nowMs)) ? Number(args.nowMs) : Date.now();
  if (dueAt != null && dueAt < nowMs) return 'Overdue';

  if (args.hasWorkOrder) return 'Work order linked';
  return 'Draft';
}

export function correctiveActionStatusHelper(args: {
  opsState?: string | null;
  followUpOwnerLabel?: string | null;
}): string {
  return formatFollowUpStatusDisplayLabel(args.opsState, ownerRoleIsUnassigned(args.followUpOwnerLabel) ? 'OPEN' : 'ACKED');
}
